// 진열관리 화면.
// 사용자 메인의 "베스트 상품"(상단)과 "전체 상품"(하단) 진열 순서를 드래그로 조정한다.
//  - 베스트: products.best_sort_order (베스트로 지정된 상품만 대상, 숨김/품절 포함)
//  - 전체: products.sort_order (전역 진열 순서, 숨김/품절 포함)
// 사용자 화면은 정렬 옵션 미선택(기본) 시 이 순서대로 노출된다.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ProductDto, ProductReorderScope } from "@oo/shared";
import { Button } from "@oo/ui";
import { PageHeader } from "@/shared/ui";
import {
  listBestProductsAdmin,
  listProductsAdmin,
  reorderProducts,
} from "@/features/catalog/catalogApi.ts";
import ProductReorderList from "@/features/catalog/ProductReorderList.tsx";

// 진열관리 메인. 베스트/전체 두 섹션으로 구성된다.
export default function MerchandisingPage() {
  const navigate = useNavigate();
  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-6 sm:px-6 sm:py-8">
      <PageHeader
        title="진열 관리"
        description="사용자 메인의 베스트/전체 상품 진열 순서를 드래그로 조정합니다. 사용자가 정렬 옵션을 누르기 전 기본 노출 순서입니다."
        actions={
          <Button variant="secondary" size="sm" onClick={() => navigate("/catalog/products")}>
            상품 목록으로
          </Button>
        }
      />

      <ReorderSection
        title="베스트 상품 진열"
        description="메인 상단 베스트 목록의 노출 순서입니다. '베스트 지정'된 상품만 표시됩니다."
        scope="best"
        queryKey={["admin", "merchandising", "best"]}
        queryFn={listBestProductsAdmin}
        emptyHint="베스트로 지정된 상품이 없습니다. 상품 화면에서 '베스트 지정'을 먼저 하세요."
      />

      <ReorderSection
        title="전체 상품 진열"
        description="메인 하단 전체 상품 목록의 기본 노출 순서입니다(최대 100개)."
        scope="all"
        queryKey={["admin", "merchandising", "all"]}
        queryFn={() => listProductsAdmin({ limit: 100, offset: 0 }).then((r) => r.products)}
        emptyHint="등록된 상품이 없습니다."
      />
    </div>
  );
}

// 진열 섹션(베스트/전체 공용). 자체 작업 상태를 들고 드래그로 순서를 바꾼 뒤 저장한다.
function ReorderSection({
  title,
  description,
  scope,
  queryKey,
  queryFn,
  emptyHint,
}: {
  title: string;
  description: string;
  scope: Exclude<ProductReorderScope, "category">;
  queryKey: (string | number)[];
  queryFn: () => Promise<ProductDto[]>;
  emptyHint: string;
}) {
  const qc = useQueryClient();
  const query = useQuery({ queryKey, queryFn });
  // 드래그로 바뀌는 작업 순서. 서버 데이터 로드/갱신 시 동기화한다.
  const [working, setWorking] = useState<ProductDto[]>([]);
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (query.data) {
      setWorking(query.data);
      setDirty(false);
    }
  }, [query.data]);

  const saveMut = useMutation({
    mutationFn: () =>
      reorderProducts({ scope, orderedIds: working.map((p) => p.id) }),
    onSuccess: async () => {
      setDirty(false);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
      // 사용자/관리자 양쪽 목록 캐시를 무효화해 새 순서가 반영되게 한다.
      await qc.invalidateQueries({ queryKey });
      await qc.invalidateQueries({ queryKey: ["admin", "products"] });
    },
  });

  return (
    <section className="flex flex-col gap-3 rounded-lg border border-ink-200 bg-ink-50 p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 className="text-base font-bold">{title}</h2>
          <p className="mt-0.5 text-xs text-ink-600">{description}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {saved ? <span className="text-xs text-success-700">저장됨</span> : null}
          <button
            type="button"
            disabled={!dirty || saveMut.isPending}
            onClick={() => saveMut.mutate()}
            className="h-9 rounded-md bg-brand-600 px-4 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-40"
          >
            {saveMut.isPending ? "저장 중..." : "순서 저장"}
          </button>
        </div>
      </div>

      {query.isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-14 animate-pulse rounded-md bg-ink-200/60" />
          ))}
        </div>
      ) : working.length === 0 ? (
        <p className="rounded-md border border-dashed border-ink-300 bg-white p-6 text-center text-sm text-ink-600">
          {emptyHint}
        </p>
      ) : (
        <ProductReorderList
          items={working}
          onReorder={(next) => {
            setWorking(next);
            setDirty(true);
          }}
        />
      )}
    </section>
  );
}
